import React, { useEffect, useState, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Clock, FileText, Shield, AlertTriangle, Mail, ChevronRight } from 'lucide-react';
const sections = [{
  id: 'standard',
  label: 'Standard Assessments'
}, {
  id: 'court',
  label: 'Court & Tribunal'
}, {
  id: 'how-to-cancel',
  label: 'How to Cancel'
}, {
  id: 'our-commitment',
  label: 'Our Commitment'
}];
const policyLinks = [{
  href: '/privacy-policy',
  label: 'Privacy Policy'
}, {
  href: '/cancellation-policy',
  label: 'Cancellation Policy',
  active: true
}, {
  href: '/terms-conditions',
  label: 'Terms & Conditions'
}];
export function CancellationPolicy() {
  const [activeSection, setActiveSection] = useState('standard');
  useEffect(() => {
    document.title = 'Cancellation Policy | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Terms for cancelling or rescheduling medical legal assessments and court attendance. Notice periods, charges, and cancellation procedures.');
    setMeta('robots', 'index, follow');
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px'
    });
    sections.forEach(({
      id
    }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-594">
      <Navigation data-id="element-595" />

      <div className="pt-32 pb-20 md:pt-40 md:pb-24" data-id="element-596">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-597">
          <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16" data-id="element-598">
            {/* Sticky Sidebar */}
            <aside className="hidden lg:block" data-id="element-599">
              <div className="sticky top-32" data-id="element-600">
                {/* Policy navigation */}
                <nav aria-label="Legal policies navigation" className="mb-8" data-id="element-601">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-602">
                    Policies
                  </p>
                  <ul className="space-y-1" data-id="element-603">
                    {policyLinks.map(link => <li key={link.href} data-id="element-604">
                        <a href={link.href} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${link.active ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-605">
                          {link.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>

                {/* Section navigation */}
                <nav aria-label="Page sections navigation" data-id="element-606">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-607">
                    On this page
                  </p>
                  <ul className="space-y-1" data-id="element-608">
                    {sections.map(section => <li key={section.id} data-id="element-609">
                        <a href={`#${section.id}`} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${activeSection === section.id ? 'bg-warm-100 text-ink-900 font-semibold' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-610">
                          {section.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Main Content — max 800px for readability */}
            <main className="max-w-[800px]" data-id="element-611">
              {/* Mobile policy nav */}
              <div className="lg:hidden flex gap-2 mb-8 overflow-x-auto pb-2" data-id="element-612">
                {policyLinks.map(link => <a key={link.href} href={link.href} className={`whitespace-nowrap text-sm py-2 px-4 rounded-full border transition-colors ${link.active ? 'bg-teal-50 border-teal-200 text-teal-700 font-semibold' : 'border-warm-200 text-ink-500 hover:border-teal-300'}`} data-id="element-613">
                    {link.label}
                  </a>)}
              </div>

              {/* Header */}
              <div className="mb-10" data-id="element-614">
                <div className="flex items-center gap-3 mb-4" data-id="element-615">
                  <div className="w-10 h-10 bg-warm-50 rounded-xl border border-warm-200 flex items-center justify-center" role="img" aria-label="Minimalist document icon with a clock symbol representing cancellation notice periods" data-id="element-616">
                    <Clock className="h-5 w-5 text-slate-600" aria-hidden="true" data-id="element-617" />
                  </div>
                  <div data-id="element-618">
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1A2B3C] tracking-tight" data-id="element-619">
                      Cancellation & Rescheduling Policy
                    </h1>
                  </div>
                </div>
                <p className="text-sm text-ink-400 font-medium mb-6" data-id="element-620">
                  Effective from January 2025 · UK Medicolegal Assessment Ltd
                </p>
                <p className="text-lg text-ink-600 leading-relaxed" data-id="element-621">
                  Effective Management of Professional Commitments.
                </p>
              </div>

              {/* Key dates callout */}
              <div className="bg-[#2D3748] text-white rounded-2xl p-6 md:p-8 mb-12" data-id="element-622">
                <div className="flex items-start gap-3 mb-4" data-id="element-623">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-624" />
                  <p className="text-sm font-bold uppercase tracking-wider text-amber-400" data-id="element-625">
                    Key Dates Summary
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-626">
                  <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-627">
                    <p className="text-2xl font-bold text-white mb-1" data-id="element-628">72 hrs</p>
                    <p className="text-sm text-slate-300" data-id="element-629">
                      Standard assessment free cancellation window
                    </p>
                  </div>
                  <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-630">
                    <p className="text-2xl font-bold text-white mb-1" data-id="element-631">24 hrs</p>
                    <p className="text-sm text-slate-300" data-id="element-632">
                      Late notice — preparation & travel charged
                    </p>
                  </div>
                  <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-633">
                    <p className="text-2xl font-bold text-white mb-1" data-id="element-634">7 days</p>
                    <p className="text-sm text-slate-300" data-id="element-635">
                      Court attendance free cancellation window
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 1: Standard Assessments */}
              <section id="standard" className="mb-14" data-id="element-636">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-637">
                  1. Standard Assessments
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-638">
                  Due to the significant time allocated for preparation and
                  report drafting, the following charges apply to cancellations
                  of scheduled medical assessments:
                </p>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden mb-8" data-id="element-639">
                  <table className="w-full" data-id="element-640">
                    <thead className="bg-warm-50" data-id="element-641">
                      <tr data-id="element-642">
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-643">
                          Notice Period
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-644">
                          Charge
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-645">
                      <tr data-id="element-646">
                        <td className="px-6 py-4 text-ink-700 font-semibold" data-id="element-647">
                          Over 72 hours' notice
                        </td>
                        <td className="px-6 py-4 text-sage-600 font-bold" data-id="element-648">
                          No charge
                        </td>
                      </tr>
                      <tr data-id="element-649">
                        <td className="px-6 py-4 text-ink-700 font-semibold" data-id="element-650">
                          Under 72 hours' notice
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-651">
                          Preparation work (billed at Hourly Rate), plus
                          incurred travel time and expenses
                        </td>
                      </tr>
                      <tr className="bg-red-50/40" data-id="element-652">
                        <td className="px-6 py-4 text-ink-900 font-bold" data-id="element-653">
                          Under 24 hours / "No-Show"
                        </td>
                        <td className="px-6 py-4 text-ink-900 font-bold" data-id="element-654">
                          Full assessment fee charged
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 2: Court & Tribunal */}
              <section id="court" className="mb-14" data-id="element-655">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-656">
                  2. Court & Tribunal Attendance
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-657">
                  Cancellations for oral evidence are subject to separate notice
                  periods, reflecting the significant preparation required for
                  court attendance:
                </p>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden mb-8" data-id="element-658">
                  <table className="w-full" data-id="element-659">
                    <thead className="bg-warm-50" data-id="element-660">
                      <tr data-id="element-661">
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-662">
                          Notice Period
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-663">
                          Charge
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-664">
                      <tr data-id="element-665">
                        <td className="px-6 py-4 text-ink-700 font-semibold" data-id="element-666">
                          Over 7 working days' notice
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-667">
                          No attendance fee. Preparation work already completed
                          is billed at the Hourly Rate.
                        </td>
                      </tr>
                      <tr className="bg-red-50/40" data-id="element-668">
                        <td className="px-6 py-4 text-ink-900 font-bold" data-id="element-669">
                          Under 7 working days' notice
                        </td>
                        <td className="px-6 py-4 text-ink-900 font-bold" data-id="element-670">
                          Full attendance fee, plus all preparation, travel, and
                          expenses
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 3: How to Cancel */}
              <section id="how-to-cancel" className="mb-14" data-id="element-671">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-672">
                  3. How to Cancel
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-673">
                  All cancellations must be submitted in writing to:
                </p>

                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-id="element-674">
                  <div className="flex items-center gap-3" data-id="element-675">
                    <Mail className="h-5 w-5 text-teal-600 flex-shrink-0" data-id="element-676" />
                    <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-bold text-lg transition-colors" data-id="element-677">
                      info@uk-medicolegal-assessment.com
                    </a>
                  </div>
                </div>

                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-678">
                  Notice is effective from the time of email receipt during
                  business hours (Monday–Friday, 9am–5pm GMT). Emails received
                  outside business hours will be treated as received at 9am on
                  the next working day.
                </p>
              </section>

              {/* Section 4: Our Commitment */}
              <section id="our-commitment" className="mb-14" data-id="element-679">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-680">
                  4. Our Commitment
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-681">
                  The Company will ensure the Expert endeavours to attend all
                  scheduled assessments and hearings. In the event of unforeseen
                  circumstances beyond our control or conflicting Court
                  requirements, we will notify the Instructing Party immediately
                  to reschedule.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-682">
                  Where the Expert is unable to attend due to circumstances
                  within the Company's control, no cancellation charges will
                  apply and a replacement appointment will be arranged at the
                  earliest opportunity.
                </p>
              </section>

              {/* Instruction Confirmation callout */}
              <div className="bg-teal-50 border border-teal-100 rounded-2xl p-6 md:p-8 mb-12" data-id="element-683">
                <div className="flex items-start gap-4" data-id="element-684">
                  <div className="w-10 h-10 bg-white rounded-xl border border-teal-200 flex items-center justify-center flex-shrink-0" data-id="element-685">
                    <FileText className="h-5 w-5 text-teal-600" data-id="element-686" />
                  </div>
                  <div data-id="element-687">
                    <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-688">
                      Instruction Confirmation
                    </h3>
                    <p className="text-ink-600 leading-relaxed text-sm" style={{
                    lineHeight: '1.8'
                  }} data-id="element-689">
                      Upon receipt of your instruction, we will issue a branded
                      PDF confirmation that includes a Key Dates Summary from
                      this policy. This ensures all parties are aware of
                      cancellation notice periods from the outset, reducing the
                      risk of late cancellations and payment disputes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 md:p-8" data-id="element-690">
                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-691">
                  Need to Cancel or Reschedule?
                </h3>
                <p className="text-ink-600 leading-relaxed mb-4" style={{
                lineHeight: '1.8'
              }} data-id="element-692">
                  Contact us as soon as possible to minimise charges and arrange
                  alternatives:
                </p>
                <p className="text-ink-700" data-id="element-693">
                  <strong data-id="element-694">Email:</strong>{' '}
                  <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-695">
                    info@uk-medicolegal-assessment.com
                  </a>
                  <br data-id="element-696" />
                  <strong data-id="element-697">Hours:</strong> Monday–Friday, 9am–5pm
                </p>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer data-id="element-698" />
    </div>;
}